import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import DateRangePresetPicker from '../components/DateRangePresetPicker';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  listQuotations,
  deleteQuotation,
  sendQuotation,
  duplicateQuotation,
  convertQuotationToPurchase,
} from '../api';
import { hasPermission } from '../utils/permissions';
import { getQuotationStatusLabel } from '../utils/quotationStatus';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';

const QuotationsListPage = () => {
  const navigate = useNavigate();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    startDate: '',
    endDate: '',
    includeDeleted: true,
  });
  const [page, setPage] = useState(1);
  const [totalQuotations, setTotalQuotations] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const { user } = useCurrentUser();
  const canCreateQuotation = hasPermission(user, 'quotation.create');
  const canUpdateQuotation = hasPermission(user, 'quotation.update');
  const canDeleteQuotation = hasPermission(user, 'quotation.delete');
  const { confirm } = useConfirmDialog();
  const pageSize = 12;

  const fetchData = async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const data = await listQuotations(branchId, {
        status: filters.status || undefined,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        isDeleted: filters.includeDeleted ? undefined : undefined,
        page,
        pageSize,
      });
      const normalized = normalizePaginatedResponse(data);
      setQuotations(normalized.data || []);
      setTotalQuotations(normalized.total || 0);
      setTotalPages(
        normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
      );
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [branchId, filters.status, filters.search, filters.startDate, filters.endDate, filters.includeDeleted, page]);

  useEffect(() => {
    setPage(1);
  }, [filters.status, filters.search, filters.startDate, filters.endDate, filters.includeDeleted]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSend = async (id, hasEmail) => {
    if (!canUpdateQuotation) return;
    if (!hasEmail) {
      const approved = await confirm({
        title: 'Enviar cotización',
        message: 'El cliente no tiene correo. ¿Marcar como enviada de todas formas?',
        confirmLabel: 'Enviar',
        cancelLabel: 'Cancelar',
        tone: 'warning',
      });
      if (!approved) return;
    } else {
      const approved = await confirm({
        title: 'Enviar cotización',
        message: '¿Enviar esta cotización por correo?',
        confirmLabel: 'Enviar',
        cancelLabel: 'Cancelar',
        tone: 'warning',
      });
      if (!approved) return;
    }
    setActionLoadingId(id);
    try {
      await sendQuotation(branchId, id, { forceSend: !hasEmail });
      notify(hasEmail ? 'Correo enviado exitosamente' : 'Cotización marcada como enviada.');
      fetchData();
    } catch (err) {
      console.error(err);
      notify('No se pudo enviar la cotización, revise las credenciales o el correo del cliente');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!canDeleteQuotation) return;
    const approved = await confirm({
      title: 'Eliminar cotización',
      message: '¿Eliminar esta cotización?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setActionLoadingId(id);
    try {
      await deleteQuotation(branchId, id);
      fetchData();
    } catch (err) {
      console.error(err);
      notify('No se pudo eliminar la cotización');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDuplicate = async (id) => {
    if (!canCreateQuotation) return;
    setActionLoadingId(id);
    try {
      const resp = await duplicateQuotation(branchId, id);
      navigate(`/branches/${branchId}/quotations/${resp.quotation.id}`);
    } catch (err) {
      console.error(err);
      notify('No se pudo duplicar la cotización');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConvert = async (id) => {
    if (!canUpdateQuotation) return;
    const approved = await confirm({
      title: 'Convertir cotización',
      message: 'Esto creará una venta sin pagar. ¿Continuar?',
      confirmLabel: 'Continuar',
      cancelLabel: 'Cancelar',
      tone: 'warning',
    });
    if (!approved) return;
    setActionLoadingId(id);
    try {
      const response = await convertQuotationToPurchase(branchId, id);
      const purchaseId = response?.purchase?.id;
      if (purchaseId) {
        notify('Cotización convertida. Redirigiendo a registrar pago.');
        navigate(`/branches/${branchId}/purchases/${purchaseId}/receipt`);
        return;
      }
      notify('Cotización convertida, pero no se encontró la venta creada.');
      fetchData();
    } catch (err) {
      console.error(err);
      notify('No se pudo convertir la cotización');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold">Cotizaciones</h1>
        <div className="flex gap-2">
          {canCreateQuotation && (
            <button
              onClick={() => navigate(`/branches/${branchId}/quotations/new`)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Nueva cotización
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 bg-gray-100 p-4 rounded mb-4">
        <div className="w-full lg:w-2/5">
          <input
            className="border rounded p-2 w-full"
            placeholder="Buscar por cliente, RUT o ID"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <div className="w-1/2 md:w-1/3 lg:w-1/5 min-w-[180px]">
          <select
            className="border rounded p-2 w-full"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="sent">Enviada</option>
            <option value="accepted">Aceptada</option>
            <option value="rejected">Rechazada</option>
            <option value="deleted">Eliminada</option>
          </select>
        </div>
        <DateRangePresetPicker
          label="Rango de fechas"
          presetLabel="Rango rapido"
          startLabel="Fecha inicio"
          endLabel="Fecha fin"
          startDate={filters.startDate}
          endDate={filters.endDate}
          onStartDateChange={(value) => setFilters({ ...filters, startDate: value })}
          onEndDateChange={(value) => setFilters({ ...filters, endDate: value })}
          containerClassName="w-full lg:w-2/5"
          labelClassName="block text-sm font-medium mb-1"
          inputLabelClassName="block text-xs text-gray-600 mb-1"
          selectClassName="border rounded p-2 w-full"
          inputClassName="border rounded p-2 w-full"
        />
        <div className="w-1/2 md:w-1/3 lg:w-1/5 min-w-[180px]">
          <button
            onClick={fetchData}
            className="w-full px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Aplicar filtros
          </button>
        </div>
      </div>

      {loading && (
        <p>
          <InlineLoading />
        </p>
      )}
      {error && <p className="text-red-600">{error}</p>}

      {quotations.length === 0 && !loading && (
        <p className="text-sm text-gray-600">No hay cotizaciones con los filtros actuales.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quotations.map((q) => {
          const hasEmail = !!q.Client?.email;
          const isLocked = q.status === 'accepted';
          const isWorking = actionLoadingId === q.id;
          return (
            <div key={q.id} className="border rounded shadow bg-white p-4 flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500"># {q.id}</div>
                <span className="text-xs px-2 py-1 rounded bg-gray-100">
                  {getQuotationStatusLabel(q.status)}
                </span>
              </div>
              <div className="font-semibold text-lg">
                {q.Client?.name || 'Detalle sin cliente'}
                {!hasEmail && <span className="ml-2 text-xs text-red-600">(sin correo)</span>}
              </div>
              <div className="text-sm text-gray-700">
                Total: ${Number(q.totalAmount || 0).toLocaleString('de-DE')}
              </div>
              <div className="text-xs text-gray-500">
                Actualizada: {new Date(q.updatedAt).toLocaleString()}
              </div>
              <div className="flex flex-wrap gap-2 mt-2 items-center">
                <button
                  onClick={() => navigate(`/branches/${branchId}/quotations/${q.id}`)}
                  className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Ver
                </button>
                {canUpdateQuotation && q.status !== 'sent' && q.status !== 'accepted' && (
                  <button
                    onClick={() => navigate(`/branches/${branchId}/quotations/${q.id}/edit`)}
                    disabled={isWorking}
                    className="px-3 py-1 bg-blue-200 rounded hover:bg-blue-300 text-sm disabled:opacity-60"
                  >
                    {isWorking ? '...' : 'Editar'}
                  </button>
                )}
                {canCreateQuotation && (
                  <button
                    onClick={() => handleDuplicate(q.id)}
                    disabled={isWorking}
                    className="px-3 py-1 bg-yellow-200 rounded hover:bg-yellow-300 text-sm disabled:opacity-60"
                  >
                    {isWorking ? '...' : 'Duplicar'}
                  </button>
                )}
                {canUpdateQuotation && q.status !== 'sent' && q.status !== 'accepted' && (
                  <button
                    onClick={() => handleSend(q.id, hasEmail)}
                    disabled={isWorking}
                    className="px-3 py-1 bg-green-200 rounded hover:bg-green-300 text-sm disabled:opacity-60"
                    title={!hasEmail ? 'El cliente no tiene correo' : undefined}
                  >
                    {isWorking ? '...' : 'Enviar'}
                  </button>
                )}
                {canUpdateQuotation && q.status === 'sent' && (
                  <button
                    onClick={() => handleConvert(q.id)}
                    disabled={isWorking}
                    className="px-3 py-1 bg-teal-200 rounded hover:bg-teal-300 text-sm disabled:opacity-60"
                  >
                    {isWorking ? '...' : 'Aceptar'}
                  </button>
                )}
                {canDeleteQuotation && (
                  <button
                    onClick={() => handleDelete(q.id)}
                    disabled={isWorking}
                    className="p-2 bg-red-200 rounded hover:bg-red-300 text-sm disabled:opacity-60"
                    title="Eliminar"
                  >
                    {isWorking ? '...' : <FiTrash2 />}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {totalQuotations > pageSize && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-6">
          <div>
            Mostrando {totalQuotations === 0 ? 0 : (page - 1) * pageSize + 1}-
            {Math.min(page * pageSize, totalQuotations)} de {totalQuotations}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Anterior
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page >= totalPages}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationsListPage;
